import Screens from "oc-threejs/Screens.js";
import Scenes from "oc-threejs/Scenes.js";
import Restraint from "./Restraint.js";
import PlaneRestraint from "./PlaneRestraint.js";
import CameraRestraint from "./CameraRestraint.js";
import BaseInteractionHandler3D from "../BaseInteractionHandler3D.js";
import Commons from "oc-commons/Commons.js";
import KeyState from "oc-interaction/KeyState.js";
import Connector from "../connectors/Connector";
import ConnectorRestraint from "./ConnectorRestraint";
import CoordinateSystem from "oc-threejs/math/CoordinateSystem";
import "../connectors/ConnectorObject3DExtensions";

/**
 * Ein InteractionHandler3D für Restraints, Snaps und Connectoren
 */
export default class RestraintInteractionHandler3D extends BaseInteractionHandler3D {

    restraints = [];
    snaps = [];
    restraintsAndSnaps = [];
    actives = [];
    snappingEnabled = true;
    /** @type {?THREE.Object3D} */
    visualizations;
    /** @type {number} */
    dragStartMs;
    needsAnimation = false;

    // die Bounding Box des DragObject3d-Objectes in lokalen Koordinaten
    /** @type {?THREE.Box3} */
    dragObject3dBoundingBoxLocal;

    boxPreviewMaterials = [false,true].map(colorWrite=>new THREE.MeshBasicMaterial({
        color: new THREE.Color(0xf0f000),
        opacity: 0.5,
        transparent: true,
        colorWrite,
        flatShading: true,
    }));

    boxPreviewGeometryCache = Commons.cache((x,y,z)=>new THREE.BoxBufferGeometry(x, y, z));

    constructor(rootContainer) {
        super(rootContainer);
    }

    initializeBoundingBox() {
        if (this.dragObject3d.getDragBoundingBox) {
            this.dragObject3dBoundingBoxLocal = this.dragObject3d.getDragBoundingBox();
        } else {
            this.dragObject3dBoundingBoxLocal = Scenes.boundingBoxLocalMatrix(this.dragObject3d, o=>!(o instanceof Connector), undefined, true);
        }
        // console.log('this.dragObject3dObject3d.position '+this.dragObject3d.position+' q '+this.dragObject3d.quaternion+' s '+this.dragObject3d.scale);
    }

    createVisualizations() {
        this.visualizations = new THREE.Group();

        const show = (x,o) => {
            if (o) {
                o.copyMatrix(x.matrixWorld);
                this.visualizations.add(o);
            }
        }

        this.restraintsAndSnaps.forEach(x => {
            x.visualization = x.createVisualization(this);
            show(x,x.visualization);
            if (STAGE.settings.showDebugObjects) {
                show(x,x.createDebugVisualization(this));
            }
        });

        STAGE.scene.add(this.visualizations);
    }

    removeVisualizations() {
        this.restraintsAndSnaps.forEach(x=>x.visualization=null);
        Scenes.remove(this.visualizations);
        this.visualizations = null;
    }

    createConnectorRestraints() {
        const handleConnectors = this.dragObject3d
            .getConnectors()
            .filter(connector => connector.isFreeForDrop());

        const sceneConnectors = Scenes.filter(
            STAGE.scene,
            o => o instanceof Connector && o.isFreeForDrop() && !handleConnectors.includes(o));

        const stored = new CoordinateSystem().from(this.dragObject3d);

        const restraints = [];

        for (let sceneConnector of sceneConnectors) {
            for (let handleConnector of handleConnectors) {
                if (this.isMatchingConnectorPair(handleConnector,sceneConnector)) {
                    handleConnector.moveTo(sceneConnector);

                    // handleConnector.updateMatrixWorld();
                    // const coordinateSystem = new CoordinateSystem();
                    // coordinateSystem.from(this.dragObject3d);

                    const restraint = this.createConnectorRestraint(handleConnector,sceneConnector);
                    if (restraint) {
                        restraints.push(restraint);
                    }
                }
            }
        }

        stored.to(this.dragObject3d);

        return restraints;
    }

    isMatchingConnectorPair(handleConnector,sceneConnector) {
        return handleConnector.matches(sceneConnector)
    }

    createConnectorRestraint(handleConnector,sceneConnector) {
        const restraint = new ConnectorRestraint({
            sceneConnector,
            handleConnector,
            matrixWorld: this.dragObject3d.matrixWorld.clone(),
            priority: 100,
            alternating: true,
        });
        return restraint;
    }

    cleanup() {
        this.removeVisualizations();
        this.restraints = [];
        this.snaps = [];
        this.restraintsAndSnaps = [];
        this.actives = [];
    }

    /**
     * Diese Methode muss überschrieben werden. Hier gibt man die Restraints und Snaps gemischt
     * für den aktuellen Dragvorgang zurück. Die Methode wird zu jedem onDrag3dBegin aufgerufen.
     * Man kann auf this.dragData oder this.dragObject3d reagieren.
     */
    getRestraintsAndSnapsForHandle() {
        return [
            new PlaneRestraint(),
            new CameraRestraint()
        ];
    }

    /** */
    onDrag3dBegin() {
        this.dragStartMs = Date.now();
        this.updateRestraintsAndSnapsForHandle();
        this.restraintsAndSnaps.forEach(x=>x.onDrag3dBegin());
        this.dragObject3d.breakAllConnections();
    }

    /** */
    onDrag3d() {
        this.performRestraint(false);
    }

    /** */
    onDrop3d() {
        this.performRestraint(true);
        Connector.updateConnectedConnectors();
    }

    /** */
    onDrag3dEnd() {
        this.restraintsAndSnaps.forEach(x=>x.onDrag3dEnd());
        this.clearActives();
        this.cleanup();
    }

    /**
     * DragEnter wird als Teil des normalen Lifecycles bei jedem
     * Drag-Vorgang geworfen.
     */
    onDrag3dSceneEnterImmediate() {
        this.createVisualizations();
        this.restraintsAndSnaps.forEach(x=>x.onDrag3dSceneEnter());
    }

    /**
     * @param real true if the mouse goes to outside
     */
    onDrag3dSceneLeave(real) {
        if (real) {
            this.restraintsAndSnaps.forEach(x=>x.onLeave(real));
        }
        this.restraintsAndSnaps.forEach(x=>x.onDrag3dSceneLeave(real));
        this.removeVisualizations();
    }


    /**
     * wird bei jedem Animator-Tick aufgerufen
     * @param {number} now
     * @param {Set} tags
     */
    onAnimatorTick(now,tags) {
        if (this.needsAnimation && !tags.has('drag')) {
            return;
        }

        const speed = 0.2;
        const fadeInMs = 200;
        const opacity = {
            min: 0.1,
            max: 0.3
        };

        const t = (now - this.dragStartMs) * speed;
        const f = t < fadeInMs ? t / fadeInMs : 1;

        const o = ((Math.sin(t * Math.PI / 180) + 1) * (opacity.max - opacity.min) / 2 + opacity.min) * f;
        this.boxPreviewMaterials.forEach(material=>{
            material.opacity = o;
            material.needsUpdate = true;
        });
    }

    getIntersectionsSortedForDistance(raycaster) {
        const intersections = this.restraints.reduce((list, restraint) => {
            if (restraint.accept()) {
                const intersection = restraint.raycastWorld(raycaster);
                if (intersection) {
                    list.push(intersection);
                }
            }
            return list;
        }, []);

        // Aufsteigend nach Distanz
        intersections.sort((a,b)=>a.distance-b.distance);

        return intersections;
    }

    getWinningIntersections(intersections) {
        const maxPrio = intersections.reduce((max,i)=>
            Math.max(max,i.restraint.additively ? Number.NEGATIVE_INFINITY : i.restraint.priority)
            ,Number.NEGATIVE_INFINITY);
        const maxPrioIntersections = intersections.filter(i=>i.restraint.priority==maxPrio);
        const hasAlternatings = maxPrioIntersections.some(i=>i.restraint.alternating);

        const winners = intersections.filter(i=>i.restraint.additively);

        let bestIntersection = null;
        if (!hasAlternatings) {
            bestIntersection = _.first(maxPrioIntersections);
        } else {
            const minDistance = maxPrioIntersections.reduce((min,i)=>Math.min(min,i.distance),Number.POSITIVE_INFINITY);
            const minDistanceTreshold = STAGE.settings.connectors.box.minDistanceTreshold;
            const minAlternatings = maxPrioIntersections.filter(i => Math.abs(minDistance-i.distance) < minDistanceTreshold );
            minAlternatings.sort((a,b)=>b.restraint.order-a.restraint.order);
            minAlternatings.forEach(x=>{
                if (x.restraint.visualization) {
                    x.restraint.visualization.visible = false;
                }
            });
            const i = Math.round(this.event.pageX / 20 ) % minAlternatings.length;
            bestIntersection = minAlternatings[i];
        }

        if (bestIntersection!=null) {
            winners.push(bestIntersection);
        }

        return winners;
    }

    /** */
    performRestraint(dropped) {
        const raycaster = this.getRaycaster();

        this.restraints.forEach(x=>{
            if (x.alternating && x.visualization) {
                x.visualization.visible = true;
            }
        });

        const intersections = this.getIntersectionsSortedForDistance(raycaster);

        const winners = this.getWinningIntersections(intersections);

        // Führe alle aktiven Restraints aus

        let currentActives = [];
        let anyIntersectionPerformed = false;

        // console.log(winners.map(w=>Commons.typeName(w.restraint)).join(', '));

        winners.forEach(i=>{
            currentActives.push(i.restraint);
            i.restraint.perform(raycaster,i.point,dropped);
            anyIntersectionPerformed = true;
        });

        // Führe alle aktiven Snaps aus

        if (anyIntersectionPerformed && this.withSnaps()) {
            this.snaps.forEach(snap=>{
                if (snap.accept()) {
                    if (snap.perform(dropped)) {
                        currentActives.push(snap);
                    }
                }
            });
        }

        this.dragObject3d.updateMatrixWorld();

        // Führe onEnter / onLeave - Events aus
        const newActives = currentActives.filter(x=>!this.actives.includes(x));
        const oldActives = this.actives.filter(x=>!currentActives.includes(x));
        oldActives.forEach(x=>x.onLeave(!dropped));
        newActives.forEach(x=>x.onEnter());
        this.actives.forEach(x=>x.onDrag3d());
        if (dropped) {
            currentActives.forEach(x=>x.onDrop());
        }

        this.actives = currentActives;

        // console.log(this.dragObject3d.parent);

    }

    /** */
    updateRestraintsAndSnapsForHandle() {
        this.clearActives();

        this.restraints.length=0;
        this.snaps.length=0;

        STAGE.scene.updateMatrixWorld(true);

        this.initializeBoundingBox();
        this.restraintsAndSnaps = this.getRestraintsAndSnapsForHandle();
        // console.log('this.restraintsAndSnaps',this.restraintsAndSnaps);

        let order = 0;
        this.restraintsAndSnaps.forEach(x=>x.order=order++);

        // Absteigend nach Priority
        this.restraintsAndSnaps.sort((a,b)=>b.priority-a.priority);

        this.needsAnimation = this.restraintsAndSnaps.some(x=>x instanceof ConnectorRestraint);

        this.restraintsAndSnaps.forEach(x=>x.setDragObject3d(this.dragObject3d,this.dragObject3dBoundingBoxLocal));

        this.restraintsAndSnaps.forEach(restraintOrSnap=>{
            if (restraintOrSnap instanceof Restraint) {
                this.restraints.push(restraintOrSnap);
            } else {
                this.snaps.push(restraintOrSnap);
            }
        });
    }

    updateDragDropBoxes() {
        APP.arrangement.object3d.dragDropBoxes.hideBoxes();
        APP.arrangement.object3d.dragDropBoxes.showBoxesForInterieur(this.dragObject3d.vsData);
    }

    /** */
    clearActives() {
        this.actives.forEach(x=>x.onLeave(false));
        this.actives.length=0;
    }

    /** */
    getRaycaster() {
        const mouse = Screens.getMouseInDeviceCoords(this.event);
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, STAGE.camera);
        return raycaster;
    }

    /** */
    withSnaps() {
        return this.snappingEnabled && !KeyState.isShift();
    }

    /**
     * Die Restraints ersetzen setDragObject3dPosition
     */
    setDragObject3dPosition(dropped) {
        // nothing
    }

}
